import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QuickActionButton } from "../components/dashboard/QuickActionButton";
import { PageHeader, Toast } from "../components/common";
import { useToast } from "../hooks/useToast";
import { ArchiveCompanyModal, CreateCompanyModal } from "../components/modals";
import { UserLimitBadge } from "../components/UserLimitBadge";
import { useCompanies } from "../hooks/useCompanies";
import { useDebounce } from "../hooks/useDebounce";
import {
  archiveCompany,
  updateCompanySettings,
} from "../services/company.service";
import { TABLE_CELL_STYLE, TABLE_HEADER_STYLE } from "../styles/tableStyles";

export function Companies() {
  const navigate = useNavigate();
  const { companies, loading, refreshCompanies } = useCompanies();
  const { toasts, showToast, dismissToast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLimit, setEditingLimit] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [archiveModalCompany, setArchiveModalCompany] = useState<
    (typeof companies)[0] | null
  >(null);

  const handleEditLimit = (companyId: string, currentValue: number) => {
    setEditingLimit(companyId);
    setEditValue(currentValue.toString());
  };

  const handleSaveLimit = async (companyId: string) => {
    const newLimit = parseInt(editValue);
    if (isNaN(newLimit) || newLimit < 1) {
      showToast("Please enter a valid number (minimum 1)", "error");
      return;
    }

    try {
      await updateCompanySettings(companyId, newLimit);
      setEditingLimit(null);
      await refreshCompanies();
    } catch (error) {
      console.error("Error updating user limit:", error);
      showToast("Failed to update user limit", "error");
    }
  };

  const handleCancelEdit = () => {
    setEditingLimit(null);
    setEditValue("");
  };

  const handleArchive = async (companyId: string) => {
    await archiveCompany(companyId);
    await refreshCompanies();
  };

  // Search filtering - server already filtered for active companies
  const filteredCompanies = useMemo(() => {
    if (!debouncedSearchTerm) return companies;
    const searchLower = debouncedSearchTerm.toLowerCase();
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(searchLower) ||
        company.address?.toLowerCase().includes(searchLower),
    );
  }, [companies, debouncedSearchTerm]);

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div style={{ fontSize: "18px", color: "#6b7280" }}>
          Loading companies...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "32px" }}>
      <PageHeader
        title="Companies"
        subtitle="Manage all companies on the Spechs platform"
        actions={
          <QuickActionButton
            label="View Archived"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8" /><path d="M1 3h22v5H1z" /><path d="M10 12h4" /></svg>}
            onClick={() => navigate("/archived-companies")}
            variant="secondary"
          />
        }
      />

      {/* Search and Actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <input
          type="text"
          placeholder="Search companies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: "1",
            minWidth: "250px",
            padding: "12px 16px",
            fontSize: "14px",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            outline: "none",
          }}
        />
        <QuickActionButton
          label="Add Company"
          icon={
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          }
          variant="secondary"
          onClick={() => setShowCreateModal(true)}
        />
      </div>

      {/* Companies List */}
      <div
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {filteredCompanies.length === 0 ? (
          <div
            style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}
          >
            {searchTerm
              ? "No companies found matching your search."
              : "No companies yet."}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  backgroundColor: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <th style={TABLE_HEADER_STYLE}>Company</th>
                <th style={TABLE_HEADER_STYLE}>Phone</th>
                <th style={TABLE_HEADER_STYLE}>Users</th>
                <th style={TABLE_HEADER_STYLE}>User Limit</th>
                <th style={TABLE_HEADER_STYLE}>Created</th>
                <th style={TABLE_HEADER_STYLE}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => {
                return (
                  <tr
                    key={company.id}
                    style={{
                      borderBottom: "1px solid #e5e7eb",
                      transition: "background-color 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <td style={TABLE_CELL_STYLE}>
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#111827",
                          marginBottom: "4px",
                        }}
                      >
                        {company.name}
                      </div>
                      {company.address && (
                        <div style={{ fontSize: "13px", color: "#6b7280" }}>
                          {company.address}
                        </div>
                      )}
                    </td>
                    <td style={TABLE_CELL_STYLE}>
                      <div style={{ color: "#111827" }}>
                        {company.phone || "-"}
                      </div>
                    </td>
                    <td style={TABLE_CELL_STYLE}>
                      <div style={{ color: "#111827" }}>
                        {company.currentUsers || 0}
                      </div>
                    </td>
                    <td style={TABLE_CELL_STYLE}>
                      {editingLimit === company.id ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            min="1"
                            style={{
                              width: "70px",
                              padding: "6px 8px",
                              border: "1px solid #3b82f6",
                              borderRadius: "4px",
                              fontSize: "14px",
                            }}
                            autoFocus
                          />
                          <button
                            onClick={() => handleSaveLimit(company.id)}
                            style={{
                              padding: "6px 10px",
                              backgroundColor: "#10b981",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              fontSize: "12px",
                              cursor: "pointer",
                            }}
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            style={{
                              padding: "6px 10px",
                              backgroundColor: "#ef4444",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              fontSize: "12px",
                              cursor: "pointer",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() =>
                            handleEditLimit(company.id, company.maxUsers)
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                          }}
                        >
                          <UserLimitBadge
                            currentUsers={company.currentUsers}
                            maxUsers={company.maxUsers}
                            variant="compact"
                          />
                          <span style={{ fontSize: "12px", color: "#6b7280" }}>
                            ✎
                          </span>
                        </div>
                      )}
                    </td>
                    <td style={TABLE_CELL_STYLE}>
                      <div style={{ color: "#6b7280", fontSize: "14px" }}>
                        {company.createdAt.toLocaleDateString()}
                      </div>
                    </td>
                    <td style={TABLE_CELL_STYLE}>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => navigate(`/companies/${company.id}`)}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#eff6ff",
                            color: "#3b82f6",
                            border: "1px solid #3b82f6",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => setArchiveModalCompany(company)}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#fffbeb",
                            color: "#f59e0b",
                            border: "1px solid #f59e0b",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                        >
                          Archive
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Company Modal */}
      {showCreateModal && (
        <CreateCompanyModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refreshCompanies();
          }}
        />
      )}

      {/* Archive Company Modal */}
      {archiveModalCompany && (
        <ArchiveCompanyModal
          company={archiveModalCompany}
          onClose={() => setArchiveModalCompany(null)}
          onArchive={handleArchive}
        />
      )}
      <Toast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
